import { ref, computed, watch } from 'vue';
import { SelectListItemType } from '@domain/enums/SelectListItemType';
import { SelectListItemStateType } from '@domain/enums/SelectListItemStateType';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import { UseMultiselectOptions, UseMultiselectReturn } from '@app/interfaces/useMultiselect.interface';

export const useMultiselect = ({ props, emit }: UseMultiselectOptions): UseMultiselectReturn => {
	const internalValue = ref(props.modelValue);
	const isOpen = ref(false);

	const formattedOptions = computed(() =>
		props.options.map(option => ({
			value: option.value,
			label: option.label,
			icon: option.icon,
			count: option.count,
			disabled: option.disabled ?? false
		}))
	);

	const selectItemType = computed(() =>
		props.mode === MultiselectMode.SINGLE ? SelectListItemType.RADIO : SelectListItemType.CHECKBOX
	);

	const mapOptionToSelectItem = (option: IMultiselectOption, isSelected: boolean): ISelectItem => ({
		value: option.value,
		text: option.label,
		icon: option.icon,
		count: option.count,
		state: option.disabled
			? SelectListItemStateType.DISABLED
			: isSelected
				? SelectListItemStateType.SELECTED
				: SelectListItemStateType.ENABLED
	});

	watch(() => props.modelValue, (val) => {
		internalValue.value = val;
	}, { deep: true });

	watch(internalValue, (val) => {
		emit('update:modelValue', val);
	}, { deep: true });

	const onOpen = (): void => {
		isOpen.value = true;
		emit('open');
	};

	const onClose = (): void => {
		isOpen.value = false;
		emit('close');
	};

	return {
		internalValue,
		isOpen,
		formattedOptions,
		selectItemType,
		mapOptionToSelectItem,
		onOpen,
		onClose
	};
};
